import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { withRetryOn429 } from '../../util/sdkRetry';
import { syncSoldOutFlag } from '../../util/soldOutSync';
import { isFieldForCategory } from '../../util/fieldHelpers';
import { domesticPaddingApplies, internationalPaddingApplies } from '../../util/shippingPadding';
import { fetchShippingCarriers } from '../../ducks/shipping.duck';
import { restoreSelection } from '../ManageListingsPage/ManageListingsPage.duck';
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
} from '../../util/types';

const { UUID, Money } = sdkTypes;

const BATCH_SIZE = 25;

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/BulkEditListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/BulkEditListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/BulkEditListingsPage/FETCH_LISTINGS_ERROR';

export const SET_DIRTY_FIELD = 'app/BulkEditListingsPage/SET_DIRTY_FIELD';
export const CLEAR_SAVED_FIELDS = 'app/BulkEditListingsPage/CLEAR_SAVED_FIELDS';

export const SET_VISIBLE_COLUMNS = 'app/BulkEditListingsPage/SET_VISIBLE_COLUMNS';

export const SAVE_REQUEST = 'app/BulkEditListingsPage/SAVE_REQUEST';
export const SAVE_PROGRESS = 'app/BulkEditListingsPage/SAVE_PROGRESS';
export const SAVE_COMPLETE = 'app/BulkEditListingsPage/SAVE_COMPLETE';
export const SAVE_CANCEL = 'app/BulkEditListingsPage/SAVE_CANCEL';
export const SAVE_RESET = 'app/BulkEditListingsPage/SAVE_RESET';
export const RESET_PAGE = 'app/BulkEditListingsPage/RESET_PAGE';

// ================ Helpers ================ //

const COLUMNS_STORAGE_KEY = 'BulkEditListingsPage_visibleColumns';

const DEFAULT_COLUMNS = [
  'title', 'description', 'images', 'status', 'category', 'price', 'stock', 'condition',
];

// The seller's saved column choice, or null (none saved, unreadable, or no storage)
const loadColumnsFromStorage = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(COLUMNS_STORAGE_KEY));
    return Array.isArray(stored) && stored.every(c => typeof c === 'string') ? stored : null;
  } catch (e) {
    return null;
  }
};

const saveColumnsToStorage = columns => {
  try {
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columns));
  } catch (e) {
    // localStorage may be unavailable
  }
};

// ================ Reducer ================ //

const initialState = {
  listings: [],
  fetchInProgress: false,
  fetchError: null,
  // The listings of the batch that failed to load, for Try again
  fetchErrorIds: [],
  // Selected listings that can't be opened (deleted, or not the seller's)
  unavailableIds: [],
  dirtyFields: {},
  // The seller's saved choice is read in the browser, as the page mounts (restoreVisibleColumns)
  visibleColumns: DEFAULT_COLUMNS,
  saveInProgress: false,
  saveCurrentIndex: 0,
  saveTotalCount: 0,
  saveResults: [],
  saveCancelled: false,
};

const bulkEditListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null, fetchErrorIds: [] };

    case FETCH_LISTINGS_SUCCESS: {
      // Merge: update existing listings, append new ones
      const incomingMap = new Map(payload.map(l => [l.id.uuid, l]));
      const updated = state.listings.map(l =>
        incomingMap.has(l.id.uuid) ? incomingMap.get(l.id.uuid) : l
      );
      // Append any truly new listings
      const existingIds = new Set(state.listings.map(l => l.id.uuid));
      const newListings = payload.filter(l => !existingIds.has(l.id.uuid));
      const unavailable = (action.meta?.unavailableIds || []).filter(
        id => !state.unavailableIds.includes(id)
      );
      return {
        ...state,
        listings: [...updated, ...newListings],
        unavailableIds: [...state.unavailableIds, ...unavailable],
        fetchInProgress: false,
      };
    }

    case FETCH_LISTINGS_ERROR:
      return {
        ...state,
        fetchInProgress: false,
        fetchError: payload,
        fetchErrorIds: action.meta?.listingIds || [],
      };

    case SET_DIRTY_FIELD: {
      const { listingId, fieldName, value } = payload;
      const listingDirty = state.dirtyFields[listingId] || {};
      return {
        ...state,
        dirtyFields: {
          ...state.dirtyFields,
          [listingId]: { ...listingDirty, [fieldName]: value },
        },
      };
    }

    // After a save: clears each saved field that still holds the value saved. A field edited
    // again while the save ran keeps its newer value, to be saved next.
    case CLEAR_SAVED_FIELDS: {
      const newDirty = { ...state.dirtyFields };
      Object.entries(payload).forEach(([listingId, saved]) => {
        const current = newDirty[listingId];
        if (!current) return;
        const remaining = Object.keys(current)
          .filter(field => !(field in saved) || current[field] !== saved[field])
          .reduce((acc, field) => ({ ...acc, [field]: current[field] }), {});
        if (Object.keys(remaining).length > 0) {
          newDirty[listingId] = remaining;
        } else {
          delete newDirty[listingId];
        }
      });
      return { ...state, dirtyFields: newDirty };
    }

    case SET_VISIBLE_COLUMNS:
      return { ...state, visibleColumns: payload };

    case SAVE_REQUEST:
      return {
        ...state,
        saveInProgress: true,
        saveCurrentIndex: 0,
        saveTotalCount: payload,
        saveResults: [],
        saveCancelled: false,
      };
    case SAVE_PROGRESS:
      return {
        ...state,
        saveCurrentIndex: state.saveCurrentIndex + 1,
        saveResults: [...state.saveResults, payload.result],
      };
    case SAVE_COMPLETE:
      return { ...state, saveInProgress: false };
    case SAVE_CANCEL:
      return { ...state, saveCancelled: true };
    case SAVE_RESET:
      return {
        ...state,
        saveInProgress: false,
        saveCurrentIndex: 0,
        saveTotalCount: 0,
        saveResults: [],
        saveCancelled: false,
      };

    case RESET_PAGE:
      return { ...initialState, visibleColumns: state.visibleColumns };

    default:
      return state;
  }
};

export default bulkEditListingsPageReducer;

// ================ Action creators ================ //

export const setDirtyField = (listingId, fieldName, value) => ({
  type: SET_DIRTY_FIELD,
  payload: { listingId, fieldName, value },
});

export const clearSavedFields = savedByListing => ({
  type: CLEAR_SAVED_FIELDS,
  payload: savedByListing,
});

export const setVisibleColumns = columns => ({
  type: SET_VISIBLE_COLUMNS,
  payload: columns,
});

// The seller's column choice, remembered in this browser
export const saveVisibleColumns = columns => dispatch => {
  saveColumnsToStorage(columns);
  dispatch(setVisibleColumns(columns));
};

// Shows the columns the seller chose last time in this browser, if any
export const restoreVisibleColumns = () => dispatch => {
  const stored = loadColumnsFromStorage();
  if (stored) dispatch(setVisibleColumns(stored));
};

export const cancelSave = () => ({ type: SAVE_CANCEL });
export const resetSave = () => ({ type: SAVE_RESET });
export const resetPage = () => ({ type: RESET_PAGE });

// ================ Thunks ================ //

// Number of listings to save in parallel. Sharetribe tolerates this comfortably;
// withRetryOn429 handles any rare backpressure.
const SAVE_CONCURRENCY = 4;

const runWithConcurrency = async (items, concurrency, worker) => {
  let nextIdx = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (true) {
      const myIdx = nextIdx++;
      if (myIdx >= items.length) break;
      await worker(items[myIdx], myIdx);
    }
  });
  await Promise.all(runners);
};

// Reshape an SDK response (update / transition / show) into the denormalized
// listing the rest of the app expects, with images and currentStock attached.
const denormalizeListing = response => {
  const listing = response.data.data;
  const included = response.data.included || [];
  const imageRefs = listing.relationships?.images?.data || [];
  listing.images = imageRefs
    .map(ref => included.find(i => i.type === 'image' && i.id.uuid === ref.id.uuid))
    .filter(Boolean);
  const stockRef = listing.relationships?.currentStock?.data;
  if (stockRef) {
    listing.currentStock = included.find(i => i.id.uuid === stockRef.id.uuid) || null;
  }
  return listing;
};

// Number of listings to load in parallel
const FETCH_CONCURRENCY = 4;

/**
 * Fetch a batch of listings by IDs, in their order. A listing that can't be opened (deleted, or
 * not the seller's) is left out and counted in unavailableIds; any other failure fails the batch,
 * which can be loaded again (fetchErrorIds).
 */
export const fetchListingsBatch = listingIds => async (dispatch, getState, sdk) => {
  dispatch({ type: FETCH_LISTINGS_REQUEST });

  try {
    const loaded = new Array(listingIds.length).fill(null);
    const unavailableIds = [];
    await runWithConcurrency(listingIds, FETCH_CONCURRENCY, async (id, i) => {
      try {
        const response = await withRetryOn429(() =>
          sdk.ownListings.show({
            id: new UUID(id),
            include: ['images', 'currentStock'],
          })
        );
        loaded[i] = denormalizeListing(response);
      } catch (e) {
        if ([403, 404].includes(e?.status)) {
          unavailableIds.push(id);
        } else {
          throw e;
        }
      }
    });
    dispatch({
      type: FETCH_LISTINGS_SUCCESS,
      payload: loaded.filter(Boolean),
      meta: { unavailableIds },
    });
  } catch (e) {
    dispatch({
      type: FETCH_LISTINGS_ERROR,
      payload: storableError(e),
      meta: { listingIds },
    });
  }
};

/**
 * Save all dirty fields, SAVE_CONCURRENCY listings at a time. The table stays editable: what
 * is saved is the dirty values as the save started.
 */
export const saveAllDirty = (listingFieldsConfig = []) => async (dispatch, getState, sdk) => {
  const { dirtyFields, listings } = getState().BulkEditListingsPage;
  const dirtyListingIds = Object.keys(dirtyFields);

  dispatch({ type: SAVE_REQUEST, payload: dirtyListingIds.length });

  const saveOne = async listingId => {
    if (getState().BulkEditListingsPage.saveCancelled) return;

    const dirty = dirtyFields[listingId];
    const listing = listings.find(l => l.id.uuid === listingId);

    try {
      // Stock lives on a separate endpoint and can run in parallel with the
      // listing update; kick it off and await before we report success.
      const stockPromise = (async () => {
        if (dirty.stock !== undefined && listing) {
          const oldTotal = listing.currentStock?.attributes?.quantity ?? 0;
          const newTotal = dirty.stock;
          if (newTotal !== null && newTotal !== oldTotal) {
            await withRetryOn429(() =>
              sdk.stock.compareAndSet(
                { listingId: new UUID(listingId), oldTotal, newTotal },
                { expand: true }
              )
            );
            await syncSoldOutFlag(sdk, new UUID(listingId), newTotal).catch(() => {});
          }
        }
      })();

      // Build update payload for non-stock, non-status fields
      const updateData = {};
      const publicDataUpdates = {};

      if (dirty.title !== undefined) updateData.title = dirty.title;
      if (dirty.price !== undefined) {
        updateData.price = new Money(dirty.price.amount, dirty.price.currency);
      }

      if (dirty.description !== undefined) {
        const plain = (dirty.description || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        updateData.description = plain;
        publicDataUpdates.descriptionHtml = dirty.description;
      }

      // Image uploads run in parallel; existing images pass through by id.
      if (dirty.images !== undefined) {
        const imageIds = await Promise.all(
          dirty.images.map(async img => {
            if (img._file) {
              const resp = await withRetryOn429(() =>
                sdk.images.upload({ image: img._file }, { expand: true })
              );
              return resp.data.data.id;
            }
            return img.id;
          })
        );
        updateData.images = imageIds.filter(Boolean);
      }

      // PublicData fields (status is NOT included — it's handled above)
      [
        'condition', 'weightPounds', 'weightOunces', 'kg', 'grams',
        'unitSystem', 'acceptOffers', 'localPickup',
        'categoryLevel1', 'categoryLevel2', 'categoryLevel3', 'categoryLevel4',
      ].forEach(field => {
        if (dirty[field] !== undefined) publicDataUpdates[field] = dirty[field];
      });

      // Shipping address
      if (dirty.shippingAddress !== undefined) {
        publicDataUpdates.shippingAddress = dirty.shippingAddress;
      }

      // Handle pub_* listing fields from category popover and clear non-matching fields
      const hasCategoryChange = ['categoryLevel1', 'categoryLevel2', 'categoryLevel3', 'categoryLevel4']
        .some(f => dirty[f] !== undefined);

      if (hasCategoryChange) {
        const targetCategoryIds = [
          dirty.categoryLevel1 ?? listing?.attributes?.publicData?.categoryLevel1,
          dirty.categoryLevel2 ?? listing?.attributes?.publicData?.categoryLevel2,
          dirty.categoryLevel3 ?? listing?.attributes?.publicData?.categoryLevel3,
          dirty.categoryLevel4 ?? listing?.attributes?.publicData?.categoryLevel4,
        ].filter(Boolean);

        const KNOWN_SCHEMA_TYPES = [SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_TEXT, SCHEMA_TYPE_LONG, SCHEMA_TYPE_BOOLEAN];

        listingFieldsConfig.forEach(fieldConfig => {
          const { key, scope = 'public', schemaType } = fieldConfig;
          if (scope !== 'public' || !KNOWN_SCHEMA_TYPES.includes(schemaType)) return;

          const isForCategory = isFieldForCategory(targetCategoryIds, fieldConfig);
          if (isForCategory) {
            // Include value if dirty, otherwise keep existing
            if (dirty[key] !== undefined) {
              publicDataUpdates[key] = dirty[key];
            }
          } else {
            // Clear fields that don't match the new category
            publicDataUpdates[key] = null;
          }
        });
      }

      // Also handle any other dirty pub_* fields not handled above
      Object.keys(dirty).forEach(key => {
        if (publicDataUpdates[key] === undefined &&
            !['title', 'price', 'description', 'stock', 'status', 'images',
              'dimensionLength', 'dimensionWidth', 'dimensionHeight',
              'condition', 'weightPounds', 'weightOunces', 'kg', 'grams',
              'unitSystem', 'acceptOffers', 'localPickup',
              'categoryLevel1', 'categoryLevel2', 'categoryLevel3', 'categoryLevel4',
              'shippingAddress',
              'domesticShippingType', 'isFreeShipping', 'ownShippingPrice', 'domesticHandlingCost',
              'internationalShippingType', 'isFreeInternationalShipping',
              'ownInternationalShippingPrice', 'internationalHandlingCost',
              'isInternationalShippingDisabled',
              'domesticCarriers', 'internationalCarriers'].includes(key)) {
          publicDataUpdates[key] = dirty[key];
        }
      });

      // Dimensions stored as nested object
      if (dirty.dimensionLength !== undefined || dirty.dimensionWidth !== undefined || dirty.dimensionHeight !== undefined) {
        const currentDims = listing?.attributes?.publicData?.dimensions || {};
        publicDataUpdates.dimensions = {
          length: dirty.dimensionLength ?? currentDims.length ?? null,
          width: dirty.dimensionWidth ?? currentDims.width ?? null,
          height: dirty.dimensionHeight ?? currentDims.height ?? null,
        };
      }

      // Domestic shipping params stored as nested object
      if (dirty.domesticShippingType !== undefined || dirty.isFreeShipping !== undefined ||
          dirty.ownShippingPrice !== undefined || dirty.domesticHandlingCost !== undefined) {
        const currentDomestic = listing?.attributes?.publicData?.domesticParams || {};
        publicDataUpdates.domesticParams = {
          ...currentDomestic,
          ...(dirty.domesticShippingType !== undefined && { domesticCostType: dirty.domesticShippingType }),
          ...(dirty.isFreeShipping !== undefined && { isFreeShipping: dirty.isFreeShipping }),
          ...(dirty.ownShippingPrice !== undefined && { ownShippingPrice: dirty.ownShippingPrice.amount / 100 }),
          ...(dirty.domesticHandlingCost !== undefined && { domesticHandlingCost: dirty.domesticHandlingCost.amount / 100 }),
        };
        // Padding only for calculated shipping: the server charges whatever is stored
        if (!domesticPaddingApplies(publicDataUpdates.domesticParams)) {
          publicDataUpdates.domesticParams.domesticHandlingCost = 0;
        }
      }

      // International shipping params stored as nested object
      if (dirty.internationalShippingType !== undefined || dirty.isFreeInternationalShipping !== undefined ||
          dirty.ownInternationalShippingPrice !== undefined || dirty.internationalHandlingCost !== undefined ||
          dirty.isInternationalShippingDisabled !== undefined) {
        const currentIntl = listing?.attributes?.publicData?.internationalParams || {};
        publicDataUpdates.internationalParams = {
          ...currentIntl,
          ...(dirty.internationalShippingType !== undefined && { internationalCostType: dirty.internationalShippingType }),
          ...(dirty.isFreeInternationalShipping !== undefined && { isFreeInternationalShipping: dirty.isFreeInternationalShipping }),
          ...(dirty.ownInternationalShippingPrice !== undefined && { ownInternationalShippingPrice: dirty.ownInternationalShippingPrice.amount / 100 }),
          ...(dirty.internationalHandlingCost !== undefined && { internationalHandlingCost: dirty.internationalHandlingCost.amount / 100 }),
          ...(dirty.isInternationalShippingDisabled !== undefined && { isInternationalShippingDisabled: dirty.isInternationalShippingDisabled }),
        };
        if (!internationalPaddingApplies(publicDataUpdates.internationalParams)) {
          publicDataUpdates.internationalParams.internationalHandlingCost = 0;
        }
      }

      // Shipping carriers stored as nested object
      if (dirty.domesticCarriers !== undefined || dirty.internationalCarriers !== undefined) {
        const currentCarriers = listing?.attributes?.publicData?.shippingCarriers || {};
        publicDataUpdates.shippingCarriers = {
          ...currentCarriers,
          ...(dirty.domesticCarriers !== undefined && { domesticCarriers: dirty.domesticCarriers }),
          ...(dirty.internationalCarriers !== undefined && { internationalCarriers: dirty.internationalCarriers }),
        };
      }

      if (Object.keys(publicDataUpdates).length > 0) {
        updateData.publicData = publicDataUpdates;
      }

      const expandOpts = { expand: true, include: ['images', 'currentStock'] };
      let latestResponse = null;

      if (Object.keys(updateData).length > 0) {
        latestResponse = await withRetryOn429(() =>
          sdk.ownListings.update({ id: new UUID(listingId), ...updateData }, expandOpts)
        );
      }

      // Status transitions, after field updates. Each transition response is
      // the freshest view of the listing.
      if (dirty.status !== undefined && listing) {
        const currentState = listing.attributes.state;
        const targetStatus = dirty.status;
        const uid = new UUID(listingId);

        if (currentState === 'draft' && targetStatus === 'published') {
          latestResponse = await withRetryOn429(() => sdk.ownListings.publishDraft({ id: uid }, expandOpts));
        } else if (currentState === 'draft' && targetStatus === 'closed') {
          await withRetryOn429(() => sdk.ownListings.publishDraft({ id: uid }, expandOpts));
          latestResponse = await withRetryOn429(() => sdk.ownListings.close({ id: uid }, expandOpts));
        } else if (currentState === 'published' && targetStatus === 'closed') {
          latestResponse = await withRetryOn429(() => sdk.ownListings.close({ id: uid }, expandOpts));
        } else if (currentState === 'closed' && targetStatus === 'published') {
          latestResponse = await withRetryOn429(() => sdk.ownListings.open({ id: uid }, expandOpts));
        }
      }

      // Wait for stock (kicked off in parallel above)
      await stockPromise;

      // Use whichever write response is freshest. If nothing was written
      // (shouldn't happen for a dirty listing) fall back to a refetch.
      const refreshedListing = latestResponse
        ? denormalizeListing(latestResponse)
        : denormalizeListing(
            await withRetryOn429(() =>
              sdk.ownListings.show({ id: new UUID(listingId), include: ['images', 'currentStock'] })
            )
          );

      dispatch({ type: FETCH_LISTINGS_SUCCESS, payload: [refreshedListing] });
      dispatch({
        type: SAVE_PROGRESS,
        payload: { result: { listingId, status: 'success' } },
      });
    } catch (e) {
      const errorMessage = e?.data?.errors?.[0]?.title || e?.message || null;
      dispatch({
        type: SAVE_PROGRESS,
        payload: { result: { listingId, status: 'error', error: errorMessage } },
      });
    }
  };

  await runWithConcurrency(dirtyListingIds, SAVE_CONCURRENCY, saveOne);

  // Clear what was saved; edits made meanwhile stay to be saved
  const results = getState().BulkEditListingsPage.saveResults;
  const saved = results
    .filter(r => r.status === 'success')
    .reduce((acc, r) => ({ ...acc, [r.listingId]: dirtyFields[r.listingId] }), {});
  if (Object.keys(saved).length > 0) {
    dispatch(clearSavedFields(saved));
  }

  dispatch({ type: SAVE_COMPLETE });
};

/**
 * Initialize the page: set listing IDs and fetch first batch.
 */
export const initBulkEdit = listingIds => async (dispatch, getState, sdk) => {
  // Fetch first batch
  const firstBatch = listingIds.slice(0, BATCH_SIZE);
  await dispatch(fetchListingsBatch(firstBatch));
};

/**
 * The page's data: the first batch of the listings selected on the manage-listings page, and
 * the carriers its shipping columns offer. After a reload the selection comes from this tab's
 * session storage (read in the browser only). With nothing selected there is nothing to load;
 * the page goes back to manage listings.
 */
export const loadData = () => (dispatch, getState) => {
  dispatch(resetPage());
  dispatch(restoreSelection());
  const listingIds = getState().ManageListingsPage?.selectedListingIds || [];
  if (listingIds.length === 0) return Promise.resolve();
  return Promise.all([dispatch(initBulkEdit(listingIds)), dispatch(fetchShippingCarriers({}))]);
};
